import { useState } from "react";
import { Money, Check } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { debtorApi } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";

const METODOS = [
  "Transferencia",
  "Efectivo",
  "Cheque",
  "Depósito",
  "Tarjeta",
  "Otro",
];

export default function PaymentModal({ open, onOpenChange, debtor, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [monto, setMonto] = useState("");
  const [fecha, setFecha] = useState(today);
  const [metodo, setMetodo] = useState("Transferencia");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  if (!debtor) return null;

  const saldo = Number(
    debtor.saldo_pendiente ?? debtor.monto_original ?? debtor.monto_deuda ?? 0
  );
  const original = Number(debtor.monto_original ?? debtor.monto_deuda ?? 0);
  const abonado = Number(debtor.total_abonado ?? 0);

  const reset = () => {
    setMonto("");
    setFecha(today);
    setMetodo("Transferencia");
    setNota("");
  };

  const handleSubmit = async () => {
    const val = parseFloat(monto);
    if (!val || val <= 0) {
      toast.error("Ingrese un monto válido mayor a cero");
      return;
    }
    if (val > saldo + 0.01) {
      toast.error(`El abono excede el saldo pendiente (${fmtCurrency(saldo)})`);
      return;
    }
    setSaving(true);
    try {
      const r = await debtorApi.registerAbono(debtor.id, {
        monto: val,
        fecha,
        metodo,
        nota,
      });
      const saldoRest = Number(r.saldo_pendiente ?? 0);
      if (saldoRest <= 0.01) {
        toast.success("Deuda saldada por completo");
      } else {
        toast.success(
          `Abono registrado · Saldo pendiente: ${fmtCurrency(saldoRest)}`
        );
      }
      reset();
      onOpenChange(false);
      onDone?.(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar el abono");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Money size={22} weight="duotone" className="text-primary" />
            Registrar abono — {debtor.cliente_nombre}
          </DialogTitle>
          <DialogDescription>
            Registre un pago parcial o total. El saldo pendiente se recalculará automáticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 rounded-md border border-border bg-accent/30 p-3 text-center">
          <div>
            <div className="overline">Original</div>
            <div className="font-mono font-medium">{fmtCurrency(original)}</div>
          </div>
          <div>
            <div className="overline">Abonado</div>
            <div className="font-mono font-medium text-success">{fmtCurrency(abonado)}</div>
          </div>
          <div>
            <div className="overline">Saldo</div>
            <div className="font-mono font-semibold text-destructive">{fmtCurrency(saldo)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="col-span-2">
            <Label>Monto recibido (USD)</Label>
            <Input
              data-testid="payment-monto"
              type="number"
              step="0.01"
              min="0"
              max={saldo}
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder={`Máx. ${fmtCurrency(saldo)}`}
              autoFocus
            />
          </div>
          <div>
            <Label>Fecha</Label>
            <Input
              data-testid="payment-fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div>
            <Label>Método</Label>
            <Select value={metodo} onValueChange={setMetodo}>
              <SelectTrigger data-testid="payment-metodo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METODOS.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Nota (opcional)</Label>
            <Textarea
              data-testid="payment-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Nro. de comprobante, referencia, etc."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="payment-submit">
            <Check size={16} weight="bold" className="mr-2" />
            {saving ? "Registrando…" : "Registrar abono"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
